import { useCallback } from 'react';

import { useShopProvider } from '#provider/useShopProvider.js';
import { type Checkout, useShowConsumerCheckout } from '@givebacks/api/store';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Stack, notifications } from 'ui';

import { useShopStorage } from '../../hooks/useShopStorage';
import { getCheckoutOrganizationNamesFromCache } from './getCheckoutOrganizationNamesFromCache';
import { getCheckoutTrackingChannel } from './getCheckoutTrackingChannel';
import { useCheckoutForm } from './hooks/useCheckoutForm';
import { trackPendoCheckoutCompleted } from './pendoCheckoutCompleted';
import { getCartIdsFromCheckout, useSessionCheckoutForm } from './utils';

type CheckoutButtonProps = {
  checkoutId: string;
  /**
   * Callback when the checkout is successful
   */
  onSuccess: () => void;
  /** Whether to show a generic notification when the checkout is successful @default true */
  withCheckoutSuccessNotification?: boolean;
};

const CheckoutButton = ({
  checkoutId,
  onSuccess,
  withCheckoutSuccessNotification = true,
}: CheckoutButtonProps) => {
  const queryClient = useQueryClient();
  const { setForm } = useSessionCheckoutForm();
  const { removeCart } = useShopStorage();
  const { tracking } = useShopProvider();

  const { data: cartIds } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartIdsFromCheckout,
  });

  const handleSuccess = useCallback(
    (checkout: Checkout) => {
      if (!cartIds) return;
      if (withCheckoutSuccessNotification) {
        notifications.show({
          variant: 'success',
          message: 'Checkout successful!',
          /* Important, longer error messages may be coming through, so we want users to have time to read it */
          autoClose: 10 * 1000,
        });
      }
      onSuccess();
      cartIds.forEach((cartId) => {
        removeCart(cartId);
      });

      setForm({
        firstName: checkout.first_name,
        lastName: checkout.last_name,
        email: checkout.email,
      });

      if (tracking?.checkout) {
        const channel = getCheckoutTrackingChannel();
        void getCheckoutOrganizationNamesFromCache(checkout, queryClient)
          .then((organizationNamesById) => {
            trackPendoCheckoutCompleted({
              checkout,
              channel,
              organizationNamesById,
            });
          })
          .catch(() => {
            // no-op: tracking should not block checkout success
          });
      }
    },
    [cartIds, onSuccess, queryClient, removeCart, setForm, tracking?.checkout]
  );

  const { isCheckoutFormLoading, error, checkout } = useCheckoutForm({
    checkoutId,
    onSuccess: handleSuccess,
  });

  return (
    <Stack>
      <Alert.Collapse title={error} type="error" in={!!error} />
      <Button variant="gradient" onClick={() => void checkout()} loading={isCheckoutFormLoading}>
        Checkout
      </Button>
    </Stack>
  );
};
export { CheckoutButton };
